// Tenant utility functions
import { get } from './api.js'

/**
 * Checks if the AI method configuration is set to Bedrock Data Automation
 * @returns {boolean} True if AI method is Bedrock Data Automation, false otherwise
 */
export const isAiMethodBedrock = () => {
  try {
    const tenantDetails = localStorage.getItem('TenantDetails')
    if (!tenantDetails) return false

    const tenantData = JSON.parse(tenantDetails)

    const aiMethodConfig = tenantData.find((config) => config.key === 'ai_method')
    const aiMethodValue = aiMethodConfig?.value
    return aiMethodValue === 'Bedrock Data Automation'
  } catch (error) {
    return false
  }
}

/**
 * Checks if the AI method configuration is set to Textract
 * @returns {boolean} True if AI method is Textract, false otherwise
 */
export const isAiMethodTextract = () => {
  try {
    const tenantDetails = localStorage.getItem('TenantDetails')
    if (!tenantDetails) return false

    const tenantData = JSON.parse(tenantDetails)

    const aiMethodConfig = tenantData.find((config) => config.key === 'ai_method')
    const aiMethodValue = aiMethodConfig?.value
    return aiMethodValue === 'Textract'
  } catch (error) {
    return false
  }
}

/**
 * Gets the AI method value from tenant configuration
 * @returns {string|null} The AI method value or null if not found
 */
export const getAiMethodValue = () => {
  try {
    const tenantDetails = localStorage.getItem('TenantDetails')
    if (!tenantDetails) return null

    const tenantData = JSON.parse(tenantDetails)
    const aiMethodConfig = tenantData.find((config) => config.key === 'ai_method')

    return aiMethodConfig?.value || null
  } catch (error) {
    return null
  }
}

/**
 * Gets a specific tenant configuration value by key
 * @param {string} key - The configuration key to retrieve
 * @returns {any} The configuration value or null if not found
 */
export const getTenantConfigValue = (key) => {
  try {
    const tenantDetails = localStorage.getItem('TenantDetails')
    if (!tenantDetails) return null

    const tenantData = JSON.parse(tenantDetails)
    const config = tenantData.find((config) => config.key === key)

    return config?.value || null
  } catch (error) {
    return null
  }
}

/**
 * Fetches tenant configurations from API for a specific tenant ID
 * @param {number} tenantId - The tenant ID to fetch configurations for
 * @returns {Promise<Array>} Promise that resolves to array of tenant configurations
 */
export const fetchTenantConfigurations = async (tenantId) => {
  try {
    const response = await get(`/v1/tenant-configs/tenant/${tenantId}`)
    return response.data?.data || []
  } catch (error) {
    return []
  }
}

/**
 * Checks if the AI method configuration is set to Bedrock Data Automation for a specific tenant
 * @param {number} tenantId - The tenant ID to check
 * @returns {Promise<boolean>} Promise that resolves to true if AI method is Bedrock Data Automation, false otherwise
 */
export const isAiMethodBedrockForTenant = async (tenantId) => {
  try {
    const configurations = await fetchTenantConfigurations(tenantId)
    const aiMethodConfig = configurations.find((config) => config.key === 'ai_method')
    return aiMethodConfig?.value === 'Bedrock Data Automation'
  } catch (error) {
    return false
  }
}

/**
 * Checks if the AI method configuration is set to Textract for a specific tenant
 * @param {number} tenantId - The tenant ID to check
 * @returns {Promise<boolean>} Promise that resolves to true if AI method is Textract, false otherwise
 */
export const isAiMethodTextractForTenant = async (tenantId) => {
  try {
    const configurations = await fetchTenantConfigurations(tenantId)
    const aiMethodConfig = configurations.find((config) => config.key === 'ai_method')
    return aiMethodConfig?.value === 'Textract'
  } catch (error) {
    return false
  }
}

/**
 * Gets a specific tenant configuration value by key for a specific tenant
 * @param {number} tenantId - The tenant ID to fetch configuration for
 * @param {string} key - The configuration key to retrieve
 * @returns {Promise<any>} Promise that resolves to the configuration value or null if not found
 */
export const getTenantConfigValueForTenant = async (tenantId, key) => {
  try {
    const configurations = await fetchTenantConfigurations(tenantId)
    const config = configurations.find((config) => config.key === key)
    return config?.value || null
  } catch (error) {
    return null
  }
}
